import { useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import type { CSSProperties } from "react";
import type { EChartsOption, ECharts } from "echarts";

let echartsPromise: Promise<typeof import("echarts")> | null = null;
function loadECharts() {
  if (!echartsPromise) echartsPromise = import("echarts");
  return echartsPromise;
}

export type EChartHandle = {
  getInstance: () => ECharts | null;
  getPng: (pixelRatio?: number) => string | null;
};

export const EChart = forwardRef<
  EChartHandle,
  {
    option: EChartsOption;
    style?: CSSProperties;
    notMerge?: boolean;
    onEvents?: Record<string, (params: unknown) => void>;
  }
>(function EChart({ option, style, notMerge, onEvents }, ref) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const instanceRef = useRef<ECharts | null>(null);
  const eventsRef = useRef(onEvents);
  eventsRef.current = onEvents;

  useImperativeHandle(ref, () => ({
    getInstance: () => instanceRef.current,
    getPng: (pixelRatio = 2) =>
      instanceRef.current
        ? instanceRef.current.getDataURL({
            type: "png",
            pixelRatio,
            backgroundColor: "#ffffff",
          })
        : null,
  }));

  useEffect(() => {
    let disposed = false;
    let resizeObserver: ResizeObserver | null = null;

    loadECharts().then((echarts) => {
      if (disposed || !containerRef.current) return;
      const inst = echarts.init(containerRef.current, undefined, {
        renderer: "canvas",
      });
      instanceRef.current = inst;
      inst.setOption(option, { notMerge: true });
      if (eventsRef.current) {
        for (const [evt, handler] of Object.entries(eventsRef.current)) {
          inst.on(evt, (params: unknown) => handler(params));
        }
      }
      resizeObserver = new ResizeObserver(() => inst.resize());
      resizeObserver.observe(containerRef.current);
    });

    return () => {
      disposed = true;
      resizeObserver?.disconnect();
      instanceRef.current?.dispose();
      instanceRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // notMerge defaults true so that swapping chart types (bar → pie → heatmap)
    // fully replaces the previous option instead of merging old series/axes in.
    instanceRef.current?.setOption(option, { notMerge: notMerge ?? true });
  }, [option, notMerge]);

  return (
    <div
      ref={containerRef}
      style={{ width: "100%", height: "100%", minHeight: "12rem", ...style }}
    />
  );
});
